/*
<summary>
   This functional component renders the Defective Items filter dropdown popup a filter icon that toggles a Dropdown.Menu with Subject/
   Item/Classification/Classification2 fields and Clear All/Apply buttons.
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { Field, Formik, Form as FormikForm } from 'formik';
import React, { useState } from 'react';
import { Button, Col, Dropdown, Form } from 'react-bootstrap';
import validCharacters from '../../../constants/valid-characters';
import { DefectiveItemFilterInitialState } from '../../../core/initialState/filter-initial-state';
import { IDefectiveItemFilter } from '../../../models/IDefectiveItemFilter';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import { FilterValidationSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import {
  ClearFilterIcon,
  CloseFilterIcon,
  FilterIcon,
} from '../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../translations/format-message';

interface IProps {
  initialValues: IDefectiveItemFilter;
  submitHandler: (data: IDefectiveItemFilter) => void;
  filterParmas: (data: IDefectiveItemFilter, isfilterCleard: boolean) => void;
  isLoading: boolean;
  isDisable: boolean;
  isFilterCleared: boolean;
  isFilterApplied: boolean;
  setIsFilterApplied: (status: boolean) => void;
}

const FilterComponent = (props: IProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const handleToggle = (isOpen: boolean, resetForm: () => void) => {
    if (!isOpen) {
      resetForm();
    }
    setShowDropdown(isOpen);
  };

  return (
    <React.Fragment>
      <Formik
        initialValues={props.initialValues}
        enableReinitialize
        validationSchema={FilterValidationSchema}
        validateOnBlur={false}
        onSubmit={async (values: IDefectiveItemFilter) => {
          props.submitHandler(values);
          setShowDropdown(false);
          props.setIsFilterApplied(true);
        }}
      >
        {({ dirty, resetForm, values }) => {
          return (
            <Dropdown
              className={`Filter-DID ${props.isDisable ? 'disabled-li' : ''}`}
              show={showDropdown}
              onToggle={(isOpen) => handleToggle(isOpen, resetForm)}
            >
              <Dropdown.Toggle
                className={`btn btn-light ${props.isFilterApplied ? 'btn-filter-disable' : 'btn-filter'}`}
                aria-expanded="false"
                title={formatMessage('filter')}
              >
                {!props.isFilterApplied ? (
                  <FilterIcon isDisable={props.isDisable} />
                ) : (
                  <ClearFilterIcon
                    onClick={() => {
                      resetForm();
                      setShowDropdown(!showDropdown);
                    }}
                  />
                )}
              </Dropdown.Toggle>
              <FormikForm className="user-form">
                {props.isLoading ? (
                  <FormLoader loading={props.isLoading} />
                ) : null}
                <Dropdown.Menu className="filter-menu">
                  <Dropdown.ItemText className="p-0">
                    <Form.Group controlId="defectiveItemFilter">
                      <div className="filter-header border-bottom p-3 px-4">
                        <h4>
                          {formatMessage('filter')}
                          <a
                            className="clear-filter float-end"
                            onClick={() => {
                              resetForm();
                              setShowDropdown(false);
                            }}
                          >
                            <CloseFilterIcon />
                          </a>
                        </h4>
                      </div>
                      <div className="p-3 px-4 row">
                        <Col md={6}>
                          <div className="form-group mb-3">
                            <Form.Group controlId="subjectFilter">
                              <Field
                                data-testid="Subject"
                                className="form-control"
                                name="Subject"
                                autoComplete="off"
                                type="text"
                                placeholder="subject"
                                as={FormikFormInput}
                                validcharacter={validCharacters}
                              />
                            </Form.Group>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form-group mb-3">
                            <Form.Group controlId="itemFilter">
                              <Field
                                data-testid="Item"
                                className="form-control"
                                name="Item"
                                autoComplete="off"
                                type="text"
                                placeholder="item"
                                as={FormikFormInput}
                                validcharacter={validCharacters}
                              />
                            </Form.Group>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form-group mb-0">
                            <Form.Group controlId="classificationFilter">
                              <Field
                                data-testid="Classification"
                                className="form-control"
                                name="Classification"
                                autoComplete="off"
                                type="text"
                                placeholder="classification_1"
                                as={FormikFormInput}
                                validcharacter={validCharacters}
                              />
                            </Form.Group>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form-group mb-0">
                            <Form.Group controlId="classification2Filter">
                              <Field
                                data-testid="Classification2"
                                className="form-control"
                                name="Classification2"
                                autoComplete="off"
                                type="text"
                                placeholder="classification_2"
                                as={FormikFormInput}
                                validcharacter={validCharacters}
                              />
                            </Form.Group>
                          </div>
                        </Col>
                      </div>
                    </Form.Group>
                  </Dropdown.ItemText>
                  <Dropdown.Divider className="mb-0" />
                  <div className="d-flex filter-btn p-3 px-4 justify-content-between align-items-center">
                    <Button
                      className="clear-filter"
                      onClick={() => {
                        props.filterParmas(DefectiveItemFilterInitialState, true);
                        resetForm();
                        setShowDropdown(false);
                        props.setIsFilterApplied(false);
                      }}
                    >
                      {formatMessage('clear_all_filters')}
                    </Button>
                    <Button
                      variant="secondary"
                      type="submit"
                      disabled={
                        !dirty &&
                        !values.Subject &&
                        !values.Item &&
                        !values.Classification &&
                        !values.Classification2
                      }
                    >
                      {formatMessage('apply')}
                    </Button>
                  </div>
                </Dropdown.Menu>
              </FormikForm>
            </Dropdown>
          );
        }}
      </Formik>
    </React.Fragment>
  );
};

export default FilterComponent;
